import { type Role } from '~/utils/types/user'

import { type ServerResponse, axios } from '../axios'

export type UserInvitationStatus =
  | 'PENDING'
  | 'EXPIRED'
  | 'CANCELED'
  | 'COMPLETED'
  | 'RE-SENT'

export interface Invitation {
  createdBy: string
  createdDate: string
  hubName: string | null
  id: number
  organizationName: string | null
  role: Role
  roleCreatedBy: Role
  stakeholder: number
  stakeholderName: string
  status: UserInvitationStatus
  userEmail: string
}

export interface GetInvitationFilters {
  filter: string
  hubs: number[]
  organizations: number[]
  role: string
  status: UserInvitationStatus | null
  username: string
  page: number
  pageSize: number
  sortBy: string
  sortDirection: 'asc' | 'desc' | null
}

type ReportFilters = Pick<
  GetInvitationFilters,
  'filter' | 'organizations' | 'hubs' | 'role' | 'status' | 'username'
>
export interface InvitationPaginatedResponse {
  content: Invitation[]
  totalElements: number
}

export async function getInvitationsPaginated(filters: GetInvitationFilters) {
  const query = /* GraphQL */ `
    query listInvitedUsers($userActivationFilter: UserActivationFilterInput) {
      listInvitedUsers(userActivationFilter: $userActivationFilter) {
        content {
          id
          userEmail
          role
          hubName
          organizationName
          status
          stakeholder
          stakeholderName
          createdDate
          createdBy
          roleCreatedBy
        }
        totalElements
      }
    }
  `
  return await axios.post<
    ServerResponse<{ listInvitedUsers: InvitationPaginatedResponse }>
  >('/graphql', {
    query,
    variables: {
      userActivationFilter: filters,
    },
  })
}

export async function resendInvitationEmail(invitation: Invitation) {
  return await axios({
    method: 'post',
    url: '/graphql',
    data: JSON.stringify({
      query:
        'mutation resendInviteEmail($id: Long) {resendInviteEmail(id: $id){successful}}',
      variables: {
        id: invitation.id,
      },
    }),
  })
}

export async function resendInvitationEmails(invitations: number[]) {
  const response = await axios.post('/graphql', {
    query: `mutation resendInvitationEmails($ids: [Long]) { resendInvitationEmails(ids: $ids) { successful } }`,
    variables: {
      ids: invitations,
    },
  })

  return response.data.data.resendInvitationEmails
}

export async function cancelUserInvitation(invitation: Invitation) {
  return await axios({
    method: 'post',
    url: '/graphql',
    data: JSON.stringify({
      query:
        'mutation cancelUserInvitation($id: Long) {cancelUserInvitation(id: $id){successful}}',
      variables: {
        id: invitation.id,
      },
    }),
  })
}

export async function downloadUserInvitationsReport(filters: ReportFilters) {
  return await axios.post<Blob>(
    '/api/v2/users/invitationsReportXslx',
    filters,
    {
      responseType: 'blob',
    },
  )
}

export async function getUsersWithInvitations() {
  const response = await axios.post<{
    data: { listUsersWithInvitations: string[] }
  }>(
    '/graphql',
    JSON.stringify({
      query: `query  { listUsersWithInvitations }`,
    }),
  )

  return response.data.data.listUsersWithInvitations
}
